import { useState } from "react";
import {
  Box, Typography, Paper, Button, Stack, Grid2 as Grid, TextField, Alert, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Divider, CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  stockAdjustmentsApi, branchesApi, stockApi,
  type ItemDto, type StockAvailabilityDto,
  type CreateStockAdjustmentRequest,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { ItemPicker } from "@/components/Pickers";

interface DraftLine {
  key: string;
  item: ItemDto | null;
  /** Available batches for the selected (item, warehouse) pair */
  batches: StockAvailabilityDto[];
  batchId: string;
  qtyDelta: number;
  reason: string;
}

const newLine = (): DraftLine => ({
  key: crypto.randomUUID(), item: null, batches: [],
  batchId: "", qtyDelta: 0, reason: "",
});

const ADJUSTMENT_REASONS = [
  "Damage / Breakage",
  "Expiry write-off",
  "Stock count correction",
  "Theft / Loss",
  "System correction",
  "Sample / Promotional",
  "Other",
];

export function CreateStockAdjustmentPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [warehouseId, setWarehouseId] = useState<string>("");
  const [documentDate, setDocumentDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState<string>("");
  const [customReason, setCustomReason] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);
  const [postAfterCreate, setPostAfterCreate] = useState(false);

  const branches = useQuery({
    queryKey: ["branches"], queryFn: () => branchesApi.list(), staleTime: 5 * 60_000,
  });

  const finalReason = reason === "Other" ? customReason : reason;

  const updateLine = async (key: string, patch: Partial<DraftLine>) => {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };
  const removeLine = (key: string) => setLines((ls) => ls.filter((l) => l.key !== key));
  const addLine = () => setLines((ls) => [...ls, newLine()]);

  // When item or warehouse changes, fetch batches with availability
  const loadBatchesForLine = async (key: string, item: ItemDto | null) => {
    if (!item || !warehouseId) {
      updateLine(key, { item, batches: [], batchId: "" });
      return;
    }
    const batches = await stockApi.availability({ itemId: item.id, warehouseId });
    updateLine(key, { item, batches, batchId: batches[0]?.batchId ?? "" });
  };

  const validate = (): string | null => {
    if (!warehouseId) return "Pick a warehouse";
    if (!finalReason.trim()) return "Reason required";
    if (lines.length === 0) return "Add at least one line";
    for (const [i, l] of lines.entries()) {
      if (!l.item) return `Line ${i + 1}: item not selected`;
      if (!l.batchId) return `Line ${i + 1}: batch not selected`;
      if (l.qtyDelta === 0) return `Line ${i + 1}: qty delta cannot be zero`;
      // For negative deltas, ensure enough stock
      const batch = l.batches.find(b => b.batchId === l.batchId);
      if (l.qtyDelta < 0 && batch && batch.availableQty + l.qtyDelta < 0)
        return `Line ${i + 1}: not enough stock (available ${batch.availableQty}, removing ${-l.qtyDelta})`;
    }
    return null;
  };

  const mutation = useMutation({
    meta: { successMessage: "Stock adjustment created" },
    mutationFn: async () => {
      const payload: CreateStockAdjustmentRequest = {
        warehouseId, documentDate, reason: finalReason,
        notes: notes || undefined,
        lines: lines.map((l) => ({
          itemId: l.item!.id, batchId: l.batchId,
          qtyDelta: l.qtyDelta, reason: l.reason || undefined,
        })),
      };
      const r = await stockAdjustmentsApi.create(payload);
      if (postAfterCreate) await stockAdjustmentsApi.post(r.id);
      return r;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stock-adjustments"] });
      qc.invalidateQueries({ queryKey: ["stock-availability"] });
      navigate("/inventory/adjustments");
    },
  });

  const validationError = validate();

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>New stock adjustment</Typography>
      <Paper sx={{ p: 3 }}>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
        <Alert severity="warning" sx={{ mb: 2 }}>
          Posting an adjustment writes directly to the inventory ledger and is permanent.
          For damage/expiry, prefer a negative delta with a clear reason.
        </Alert>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} label="Warehouse *"
              size="small" fullWidth value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
              <option value="">— Pick branch —</option>
              {branches.data?.map((b) => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField label="Document date *" type="date" size="small" fullWidth
              value={documentDate} onChange={(e) => setDocumentDate(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Stack direction="row" spacing={1}>
              <TextField select label="Reason *" value={reason}
                onChange={(e) => setReason(e.target.value)}
                size="small" fullWidth>
                <MenuItem value="">— Select —</MenuItem>
                {ADJUSTMENT_REASONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
              {reason === "Other" && (
                <TextField label="Specify *" size="small" fullWidth
                  value={customReason} onChange={(e) => setCustomReason(e.target.value)} />
              )}
            </Stack>
          </Grid>
          <Grid size={12}>
            <TextField label="Notes" size="small" fullWidth multiline rows={1}
              value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Lines</Typography>
          <Button startIcon={<AddIcon />} onClick={addLine} size="small">Add line</Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 260 }}>Item *</TableCell>
              <TableCell sx={{ minWidth: 240 }}>Batch *</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>Qty delta *</TableCell>
              <TableCell sx={{ minWidth: 200 }}>Line note</TableCell>
              <TableCell sx={{ width: 50 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((l, idx) => {
              const selectedBatch = l.batches.find(b => b.batchId === l.batchId);
              return (
                <TableRow key={l.key}>
                  <TableCell>
                    <ItemPicker value={l.item}
                      onChange={(v) => loadBatchesForLine(l.key, v)}
                      label={`Line ${idx + 1}`} required
                      error={!l.item} />
                  </TableCell>
                  <TableCell>
                    <TextField select size="small" fullWidth value={l.batchId}
                      onChange={(e) => updateLine(l.key, { batchId: e.target.value })}
                      disabled={l.batches.length === 0}>
                      <MenuItem value="">— No batch selected —</MenuItem>
                      {l.batches.map((b) => (
                        <MenuItem key={b.batchId} value={b.batchId}>
                          {b.batchNo}{b.expiryDate ? ` · exp ${new Date(b.expiryDate).toLocaleDateString()}` : ""}
                          {" · avail "}{b.availableQty}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" value={l.qtyDelta}
                      onChange={(e) => updateLine(l.key, { qtyDelta: Number(e.target.value) || 0 })}
                      inputProps={{ step: "0.01", style: { textAlign: "right" } }}
                      helperText={selectedBatch ? `Current: ${selectedBatch.availableQty}` : ""}
                      sx={{ width: 100 }} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" fullWidth value={l.reason}
                      onChange={(e) => updateLine(l.key, { reason: e.target.value })}
                      placeholder="e.g. damaged blister" />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => removeLine(l.key)} disabled={lines.length === 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Divider sx={{ my: 3 }} />
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button onClick={() => navigate("/inventory/adjustments")}>Cancel</Button>
          <Button variant="outlined"
            disabled={!!validationError || mutation.isPending}
            startIcon={mutation.isPending && !postAfterCreate ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={() => { setPostAfterCreate(false); mutation.mutate(); }}>
            Save draft
          </Button>
          <Button variant="contained" color="error"
            disabled={!!validationError || mutation.isPending}
            startIcon={mutation.isPending && postAfterCreate ? <CircularProgress size={16} color="inherit" /> : <PostAddIcon />}
            onClick={() => { setPostAfterCreate(true); mutation.mutate(); }}>
            Post adjustment (irreversible)
          </Button>
        </Stack>
        {validationError && <Alert severity="info" sx={{ mt: 2 }}>{validationError}</Alert>}
      </Paper>
    </Box>
  );
}
